import {Component, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, ParamMap, Router} from "@angular/router";
import {ServiceService} from "../../services/service.service";
import {Service} from "../../entities/service";
import {PagerDataObject} from "../../entities/pagerDataObject";
import {Subject} from 'rxjs';
import {switchMap} from 'rxjs/operators';
import {AuthService} from 'src/app/services/auth.service';
import {Report} from "../../entities/report";
import {ReportService} from "../../services/report.service";
import {ToastrService} from "ngx-toastr";
import {NgForm} from "@angular/forms";
import {saveAs} from 'file-saver';
import {TranslateService} from '@ngx-translate/core';

@Component({
    selector: 'app-report-add-edit',
    templateUrl: './report-add-edit.component.html',
    standalone: false
})
export class ReportAddEditComponent implements OnInit {
    report: any = new Report();
    submitted = false;
    services: Service[];
    fileFormats: any = []
    mappingObjects: any = []

    @ViewChild('reportForm', {static: false}) public form: NgForm;
    metaInfo: string[];
    columns: Array<any>;
    data: PagerDataObject;
    subscription: any;
    private dataSub$ = new Subject<any>();
    query: String;
    user;
    running = false;

    ngOnDestroy(): void {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }

    ngOnInit() {
        this.auth.getCurrentUser().subscribe(user => {
            this.user = user
            this.serviceService.getTotalServiceList("").subscribe(services => {
                this.services = services;
                this.route.paramMap.pipe(
                    switchMap((params: ParamMap) => this.reportService.getReport(params.get('id'))))
                    .subscribe(report => {
                        if (report) {
                            this.report = report;
                            let index = 0

                            let columnsMeta = []
                            let columns = [];
                            this.report.columns.forEach(column => {
                                columnsMeta.push(column.key)
                                columns.push({
                                    name: column.name,
                                    tag: column.name,
                                    key: column.key,
                                    filtering: true,
                                    sorting: true,
                                    sortingOrder: 'asc'
                                })
                            })
                            this.metaInfo = columnsMeta
                            this.columns = columns;

                            report.dataSets.forEach(dataSet => {
                                this.fileFormats.push()
                                this.mappingObjects.push()
                                this.onChangeService(dataSet.service, index)
                                this.onChangeFileFormat(dataSet.fileFormat, index++)
                            })
                        }
                    });

            });
        });
    }


    constructor(private serviceService: ServiceService, private reportService: ReportService,
                private auth: AuthService,
                private router: Router, private route: ActivatedRoute,
                private alertService: ToastrService,
                private translate: TranslateService) {
        this.subscription = this.dataSub$.pipe(switchMap(filter => {
            return this.reportService.getReportResult(filter);
        }))
            .subscribe(data => {
                this.data = data;
            });
    }

    onSubmit() {
        if (this.validate()) {
            this.submitted = true;
            this.reportService.upsert$(this.report)
                .subscribe(report => {
                    this.alertService.success("Report configuration has been successfully saved.");
                    this.goToList();
                });
        }
    }

    onChangeService(value, index) {
        this.fileFormats[index] = this.services.find(item => item.id === value).fileFormats;
    }


    onChangeFileFormat(value, index) {
        this.mappingObjects[index] = (this.fileFormats[index].find(item => item.name === value).mappingObjects)
    }


    goToList() {
        this.router.navigateByUrl('/report');
    }

    run() {
        if (this.validate()) {
            this.running = true;
            this.reportService.upsert$(this.report).subscribe(report => {
                this.reportService.runReport(this.report)
                    .subscribe(
                        report => {
                            this.alertService.success("Report has been successfully executed!");
                            this.dataSub$.next(this.query + "&report_id=" + this.report.id);
                            this.running = false;
                        },
                        () => { this.running = false; }
                    );
            }, () => { this.running = false; });
        }
    }

    onGridQuery(query) {
        this.query = query
        this.dataSub$.next(query + "&report_id=" + this.report.id);
    }

    toFieldName(input: string): string {
        return input
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]+/gi, ' ')
            .split(' ')
            .filter(Boolean)
            .map((word, index) =>
                index === 0
                    ? word
                    : word.charAt(0).toUpperCase() + word.slice(1)
            )
            .join('');
    }

    generateFieldName(input: string): string {
        let name = this.toFieldName(input)
        if (/^[0-9]/.test(name)) {
            name = '_' + name;
        }
        return name;
    }

    addField() {
        this.report.columns.push({name: '', key: '', summable: false})
        this.fileFormats.push()
        this.mappingObjects.push()
    }

    removeField(index: any) {
        const confirm = window.confirm('Are you sure you want to delete the field?');
        if (confirm)
            this.report.columns.splice(index, 1);
    }

    addMapping() {
        if (this.report.columns.filter(t => t.key).length > 0) {
            this.report.dataSets.push({service: {}, fileFormat: {}, mapping: {}, filters: []})
        } else {
            this.alertService.warning('Please add report columns first')
        }
    }

    addFilter(dataSet) {
        dataSet.filters.push({})
    }

    removeFilter(dataSet, index: any) {
        dataSet.filters.splice(index, 1)
    }

    removeMapping(index: any) {
        const confirm = window.confirm('Are you sure you want to delete that Mapping?');
        if (confirm) {
            this.report.dataSets.splice(index, 1);
            this.fileFormats.splice(index, 1);
            this.mappingObjects.splice(index, 1);
        }
    }

    duplicateIndex = 0

    generateFieldKey(field: any) {
        let key = this.generateFieldName(field.name);
        if (!this.report.columns.find(column => column.key === key)) {
            field.key = key;
        } else {
            field.key = `${key}_${this.duplicateIndex++}`
        }
    }

    submit() {
        this.form.onSubmit(new Event('submit'));
    }

    readyColumns(columns: any[]) {
        return columns.filter(t => t.name && t.key)
    }

    exporting = false;

    export$() {
        this.exporting = true;
        this.reportService.export$(this.query + "&report_id=" + this.report.id).subscribe(
        data => {
            const file = new Blob([data], {});
            const filename = 'document-' + this.report.name + '-' + Date.now() + '.xlsx';
            saveAs(file, filename);
            this.alertService.success(this.translate.instant('REPORT.EXPORT_SUCCESS'));
            this.exporting = false;
        }, () => {
            this.exporting = false;
        });
    }

    validate() {
        let validations = []
        if (!this.report.name?.trim()) {
            this.alertService.warning('Please enter report name');
            validations.push('Please enter a name.')
        }
        return !validations.length
    }

    collapseMap = {'#mappingAccordion': false,'#columnsAccordion': false}
    firstTime = true;
    collapseElements(element){
        if (!this.collapseMap[element]) {
            for (const key in this.collapseMap) {
                if (key !== element){
                    if (!this.firstTime && this.collapseMap[key]) {
                        let accordionCollapseElementList = document.querySelectorAll(key + ' .collapse')
                        // @ts-ignore
                        let accordionCollapseList = [...accordionCollapseElementList].map(accordionCollapseEl => new bootstrap.Collapse(accordionCollapseEl).toggle())
                    }
                    this.collapseMap[key] = false
                }
            }
            this.firstTime = false
        }
        this.collapseMap[element] = !this.collapseMap[element]
    }

}
